import { apiDelete, apiGet, apiPatch, apiPost, apiPostFormData } from "utils/apiClient";
import { toMLLanguage } from "constants/mlApi";
import { invalidateProfileStatsCache } from "lib/profileStatsCache";
import type {
    CardSummaryConfirmResponse,
    CardSummaryImportRecord,
    CardSummaryManualOverridesByLine,
    CardSummarySyncHeaderResponse,
} from "types/CardSummaryImport";

async function previewCardSummary(
    username: string,
    file: File,
    language?: string,
): Promise<CardSummaryImportRecord> {
    const formData = new FormData();
    formData.append("invoice", file);
    if (language) {
        formData.append("language", toMLLanguage(language));
    }

    return apiPostFormData<CardSummaryImportRecord>(
        `/budget/${username}/card-summary/preview`,
        formData,
    );
}

async function listCardSummaryImports(username: string): Promise<CardSummaryImportRecord[]> {
    return apiGet<CardSummaryImportRecord[]>(`/budget/${username}/card-summary`);
}

async function getCardSummaryImport(
    username: string,
    importId: string,
): Promise<CardSummaryImportRecord> {
    return apiGet<CardSummaryImportRecord>(`/budget/${username}/card-summary/${importId}`);
}

async function updateCardSummaryOverrides(
    username: string,
    importId: string,
    manualOverrides: CardSummaryManualOverridesByLine,
): Promise<CardSummaryImportRecord> {
    return apiPatch<CardSummaryImportRecord>(
        `/budget/${username}/card-summary/${importId}`,
        { manualOverrides },
    );
}

async function confirmCardSummaryImport(
    username: string,
    importId: string,
    options?: {
        manualOverrides?: CardSummaryManualOverridesByLine;
        selectedLineIds?: string[];
        replaceExisting?: boolean;
        language?: string;
    },
): Promise<CardSummaryConfirmResponse> {
    const response = await apiPost<CardSummaryConfirmResponse>(
        `/budget/${username}/card-summary/${importId}/confirm`,
        {
            manualOverrides: options?.manualOverrides,
            selectedLineIds: options?.selectedLineIds,
            replaceExisting: options?.replaceExisting,
            language: options?.language ? toMLLanguage(options.language) : undefined,
        },
    );
    invalidateProfileStatsCache(username);
    return response;
}

async function reprocessCardSummaryImport(
    username: string,
    importId: string,
    language?: string,
): Promise<CardSummaryImportRecord> {
    return apiPost<CardSummaryImportRecord>(
        `/budget/${username}/card-summary/${importId}/reprocess`,
        { language: language ? toMLLanguage(language) : undefined },
    );
}

async function syncCardSummaryImportHeader(
    username: string,
    importId: string,
    payload: { statementDate: string; dueDate: string },
): Promise<CardSummarySyncHeaderResponse> {
    const response = await apiPost<CardSummarySyncHeaderResponse>(
        `/budget/${username}/card-summary/${importId}/sync-header`,
        payload,
    );
    invalidateProfileStatsCache(username);
    return response;
}

async function unlinkCardSummaryImport(
    username: string,
    importId: string,
    lineExternalId?: string,
): Promise<CardSummaryImportRecord> {
    return apiPost<CardSummaryImportRecord>(
        `/budget/${username}/card-summary/${importId}/unlink`,
        { lineExternalId },
    );
}

async function deleteCardSummaryImport(
    username: string,
    importId: string,
    deleteTransaction = false,
): Promise<{ deleted: boolean; importId: string }> {
    const response = await apiDelete<{ deleted: boolean; importId: string }>(
        `/budget/${username}/card-summary/${importId}`,
        { deleteTransaction },
    );
    if (deleteTransaction) {
        invalidateProfileStatsCache(username);
    }
    return response;
}

export {
    previewCardSummary,
    listCardSummaryImports,
    getCardSummaryImport,
    updateCardSummaryOverrides,
    confirmCardSummaryImport,
    reprocessCardSummaryImport,
    syncCardSummaryImportHeader,
    unlinkCardSummaryImport,
    deleteCardSummaryImport,
};
